import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Settings } from 'lucide-react';
import { useStoreData } from '../hooks/useStoreData';

const Header = ({ cartCount = 0, onCartClick, onAdminClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { settings, categories } = useStoreData();

  // Get categories that should show in navbar, sorted by sequence
  const navbarCategories = [...categories]
    .filter(cat => cat.showInNavbar === true)
    .sort((a, b) => {
      const seqA = a.sequence ?? a.id;
      const seqB = b.sequence ?? b.id;
      return seqA - seqB;
    });

  const handleNewArrivalsClick = (e) => {
    e.preventDefault();
    // If not on homepage, navigate to homepage first
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollToProducts: true, selectNewArrivals: true } });
    } else {
      // Already on homepage, just scroll and select
      const element = document.getElementById('trending-outfits');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        // Trigger New Arrivals selection after scroll
        setTimeout(() => {
          // This will be handled by ProductGrid component
          window.dispatchEvent(new CustomEvent('selectNewArrivals'));
        }, 500);
      }
    }
  };

  const handleCategoryClick = (slug) => {
    navigate(`/category/${slug}`);
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Announcement Bar */}
      <div className="bg-[#c9a86a] text-center py-2 px-4 text-sm font-medium text-black">
        {settings?.shippingMessage || 'FREE DELIVERY ALL OVER KARACHI'}
      </div>

      {/* Main Navigation */}
      <header className="bg-[#4a1a1f] text-white sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            {/* Left: Logo */}
            <div className="flex items-center gap-4">
              <a
                href="/"
                className="flex items-center"
                style={{
                  background: '#c9a86a',
                  padding: '1rem',
                  borderRadius: '50%'
                }}
              >
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/rizzahra-43273.firebasestorage.app/o/74e4297d-1681-4176-a129-faaf9c90eaec-removebg-preview.png?alt=media&token=cd033554-61ba-4194-aacf-06fc9f7b4eb0"
                  alt={settings?.storeName || "Rizzahra"}
                  className="h-12 md:h-16 w-auto object-contain"
                  style={{ width: '6rem', height: '6rem' }}
                />
              </a>
              <button
                className="lg:hidden hover:text-[#c9a86a] transition-colors"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>

            {/* Center: Navigation Menu (Desktop) */}
            <nav className="hidden lg:flex items-center gap-8 text-sm uppercase tracking-wider">
              <a
                href="#new-arrivals"
                onClick={handleNewArrivalsClick}
                className="hover:text-[#c9a86a] transition-colors cursor-pointer"
              >
                New Arrivals
              </a>
              {navbarCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.slug)}
                  className="hover:text-[#c9a86a] transition-colors"
                >
                  {category.name}
                </button>
              ))}
            </nav>

            {/* Right: Cart & Admin */}
            <div className="flex items-center gap-4">
              {onAdminClick && (
                <button
                  onClick={onAdminClick}
                  className="hover:text-[#c9a86a] transition-colors"
                  title="Admin Dashboard"
                >
                  <Settings size={22} />
                </button>
              )}
              <button onClick={onCartClick} className="relative hover:text-[#c9a86a] transition-colors">
                <ShoppingCart size={22} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#c9a86a] text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-[#3a0f14] border-t border-[#5a2a2f]">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-3 text-sm uppercase tracking-wider">
              <a
                href="#new-arrivals"
                onClick={(e) => {
                  handleNewArrivalsClick(e);
                  setIsMenuOpen(false);
                }}
                className="hover:text-[#c9a86a] transition-colors py-2 cursor-pointer"
              >
                New Arrivals
              </a>
              {navbarCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.slug)}
                  className="hover:text-[#c9a86a] transition-colors py-2 text-left"
                >
                  {category.name}
                </button>
              ))}
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;